import {Component, ElementRef, EventEmitter, Input, OnInit, ViewChild} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';
import {StromcheckAnfrage} from '../../anfrage/stromcheck.anfrage';
import {FormConfig} from '../../../form/form.config';
import {SavingsForm} from './savings.form';
import {StromcheckRequestService} from '../../services/request.service';
import {PortalService} from "../../../services/portal.service";

@Component({
  selector: 'app-savings',
  templateUrl: './savings.component.html',
})

export class SavingsComponent implements OnInit {

  /**
   * teh current anfrage;
   */
  @Input() anfrage: StromcheckAnfrage;

  /**
   * textkeyprefix for all texts
   */
  @Input() textkeyPrefix: string = 'stromcheck.ergebnis.strompreis';

  /**
   * textkeyprefix for all texts
   */
  @Input() textkeyCostSavings: string = '.text.cost.savings';

 /**
   * textkeyprefix for all texts
   */
  @Input() textkeyCo2Savings: string = '.text.co2.savings';

  /**
   * Event emitter activated always when car electricity input changes
   */
  @Input() carElectricityHasChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * result data
   */
  resultData: any;

  /**
   * Placeholder used in all texts
   */
  textPlaceholder: any = {};

  /**
   * toggle for showing the price form.
   */
  showForm: boolean = false;

  /**
   * the form for the price input
   */
  form: UntypedFormGroup;

  /**
   * formconfig for the input form
   */
  formConfig: FormConfig;

  /**
   * holds a formerror if the form is invalid.
   */
  formError: string = null;

  /**
   * toggle for the loading animation of the save button.
   */
  loading: boolean = false;

  /**
   * url for the button on the rght side.
   */
  url: string;

  /**
   * min height of card
   */
  cardMinHeight: number = 0;

  /**
   * the back card element
   */
  @ViewChild('backCard') backCard: ElementRef;

  /**
   * constructor
   * @param requestService
   * @param portalService
   */
  constructor(private requestService: StromcheckRequestService, private portalService: PortalService, private element: ElementRef) {
  }

  ngOnInit() {
    // create form
    this.formConfig = new SavingsForm();
    this.form = this.formConfig.getForm();

    // preset data
    this.formConfig.setData(this.anfrage.userInput);

    //check if there is url in portal settings and get it for right button
    if(this.portalService.getConfig('stromcheck.oekostrom.tarife')){
      this.url = this.portalService.getConfig('stromcheck.oekostrom.tarife');
    }

    //init placeholder and textkeys for cost and co2 savings
    this.initPlaceholder();
    this.updateTextCostSavings();
    this.updateTextCo2Savings();

    //init card height
    this.updateCardHeight(this.element);

    //update savings placeholder if car electricity changes
    this.carElectricityHasChanged.subscribe(() => {
      //get updated values of placeholder from backend if class changes
      this.initPlaceholder();
      //update textkeys for cost savings in different cases
      this.updateTextCostSavings();
      //update textkeys for co2 savings
      this.updateTextCo2Savings();
    })
  }

  /**
   * initialize/update the placeholders for the text.
   *
   */
  initPlaceholder(): void {
    // get the resultdata
    this.resultData = this.anfrage?.result?.data;
    // get placeholder from backend
    this.textPlaceholder = this.anfrage?.result?.placeholder ?? {};
  }

  /**
   * if the button of the price form is clicked, save data to backend.
   */
  saveForm() {
    this.formError = null;
    //update card height
    this.updateCardHeight(this.element);

    if(this.form.valid) {
      // set data from form on anfrage
      this.anfrage.userInput = {...this.anfrage.userInput, ...this.formConfig.getData()};

      // send data to backend and show result if ready
      this.loading = true;
      this.requestService.backendService.getRequestId().subscribe(requestId => {
        // calculate result
        this.requestService.backendService.result(this.anfrage, requestId).subscribe(data => {
          // save data
          this.anfrage.result = data;
          this.requestService.setAnfrage(this.anfrage);

          // update placeholder
          this.initPlaceholder();
          // update texkeys if there is an input to strompreis
          this.updateTextCostSavings();
          //update textkeys for co2 savings
          this.updateTextCo2Savings();
          //update card height
          this.updateCardHeight(this.element);

          // stop loading and flip back
          this.loading = false;
          this.showForm = false;
        }, error => {
          this.loading = false;
          // use error from the backend here
          this.formError = error.error?.result?.status?.errors[0]?.code;
          //update card height in case of an form error
          this.updateCardHeight(this.element);
        });
      }, error => {
        // generic connection error
        this.loading = false;
        this.formError = 'internal';
        //update card height in case of an form error
        this.updateCardHeight(this.element);
      });
    }
    else {
      // form is not valid, show error message
      this.formError = 'form_invalid';
      //update card height in case of an form error
      this.updateCardHeight(this.element);
    }
  }

  /**
   * update text keys for cost savings box
   */
  updateTextCostSavings(){
    //update textkeys for savings box
    this.textkeyCostSavings = '.text.cost.savings';
    //check if cost savings === 0
    if(this.anfrage?.result?.data?.stromkosten_einsparung === 0) {
      // check if no arbeitspreis_cent_kwh
      if(!this.anfrage?.userInput?.arbeitspreis_cent_kwh) {
        //check if green electricity
        this.textkeyCostSavings += this.anfrage?.result?.ratings?.cost_savings_zero_with_green_electricity === true? '.0.oekostrom.ja' : '.0.oekostrom.nein';
      }
      //check if arbeitspreis_cent_kwh > 0
      else if(this.anfrage?.userInput?.arbeitspreis_cent_kwh > 0) {
        //check if green electricity and set customized text with deviation from input electricity price
        this.textkeyCostSavings += (this.anfrage?.result?.ratings?.cost_savings_zero_with_green_electricity === true? '.0.oekostrom.ja' : '.0.oekostrom.nein') + '.deviation.electricity.price' + this.anfrage?.result?.ratings.deviation_electricity_price;
      }
    }
    //check if cost savings > 0 and arbeitspreis_cent_kwh > 0 and set customized text with deviation from input electricity price
    else if(this.anfrage?.result?.data?.stromkosten_einsparung > 0 && this.anfrage?.userInput?.arbeitspreis_cent_kwh > 0) {
      this.textkeyCostSavings += '.deviation.electricity.price' + this.anfrage?.result?.ratings.deviation_electricity_price;
    }
  }

  /**
   * update text keys for co2 savings box
   */
  updateTextCo2Savings() {
    let postfix = '.nein';
    this.textkeyCo2Savings = '.text.co2.savings' + postfix;

    if (this.anfrage?.userInput?.nutzung_oekostrom === 'ja' && this.anfrage?.result?.data?.co2_emission_oekostrom_wechsel > 0) {
      postfix = '.ja';
    }
    else if(this.anfrage?.result?.data?.co2_emission_oekostrom_wechsel === 0) {
      postfix = '.0';
    }
    this.textkeyCo2Savings = '.text.co2.savings' + postfix;
  }

  /**
   * update card height
   */
  updateCardHeight(element: ElementRef): void {
    setTimeout(() => {
      this.cardMinHeight = this.backCard.nativeElement.getBoundingClientRect().height;
    }, 0);
  }

}
